import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { LogIn, LogOut, Monitor, Moon, RefreshCw, Settings, Sun } from 'lucide-react';
import { Button } from '@/components/ui';
import { SidebarTrigger } from '@/components/ui/Sidebar';
import { DashboardModePill } from '@/components/layout/DashboardModePill';
import { DashboardBreadcrumbs } from '@/components/layout/DashboardBreadcrumbs';
import { useDashboardModeSwitch } from '@/hooks/useDashboardModeSwitch';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/DropdownMenu';
import { PageHelpPopover } from '@/components/tour/PageHelpPopover';
import { NotificationsPopover } from '@/components/public/NotificationsPopover';
import { UserAvatar } from '@/components/UserAvatar';
import { useThemeStore, type ThemeMode } from '@/store/theme';
import { usePageHeaderContext } from '@/components/layout/PageHeaderContext';
import { useAcademyPaths } from '@/hooks/useAcademyPaths';
import { useGuardianAcademyStore } from '@/store/guardianAcademy';
import { useCoachAcademyStore } from '@/store/coachAcademy';
import { academyProfileService } from '@/services/academyProfile';
import { hasRole } from '@court-sync/shared';
import { TOUR_STEP_IDS } from '@/lib/tour-constants';
import { resolveDashboardBreadcrumbs } from '@/lib/dashboardBreadcrumbs';
import { useUnifiedConsumerNav } from '@/hooks/useUnifiedConsumerNav';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { useOrgMemberStore } from '@/store/orgMember';
interface DashboardContentChromeProps {
  dashboardRole:
    | 'academy_owner'
    | 'academy_admin'
    | 'club_owner'
    | 'club_admin'
    | 'guardian'
    | 'admin'
    | 'coach';
  academySlug?: string;
}

const THEME_MENU: { value: ThemeMode; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

export function DashboardContentChrome({ dashboardRole, academySlug }: DashboardContentChromeProps) {
  const { user, logout } = useAuthStore();
  const { theme, setTheme } = useThemeStore();
  const navigate = useNavigate();
  const location = useLocation();
  const modeSwitch = useDashboardModeSwitch(dashboardRole, academySlug);
  const { header } = usePageHeaderContext();

  const guardianAcademy = useGuardianAcademyStore((s) => s.academy);
  const coachAcademy = useCoachAcademyStore((s) => s.academy);
  const academyForPaths =
    dashboardRole === 'guardian' ? guardianAcademy : dashboardRole === 'coach' ? coachAcademy : null;
  const { p, login } = useAcademyPaths(academyForPaths);

  const [hasMultipleAcademies, setHasMultipleAcademies] = useState(false);

  const hasGuardianAccess = user ? hasRole(user, 'guardian') : false;

  const notificationsViewAllPath = useMemo(() => {
    if (dashboardRole === 'guardian' && academySlug) {
      return `/academy/${academySlug}/notifications`;
    }
    if (dashboardRole === 'coach' && academySlug) {
      return `/academy/${academySlug}/coach/notifications`;
    }
    return '/notifications';
  }, [dashboardRole, academySlug]);

  const settingsPath =
    dashboardRole === 'guardian' && p
      ? p('/settings')
      : dashboardRole === 'coach' && p
        ? p('/coach/settings')
        : '/settings';

  const isSettingsActive = location.pathname.startsWith(settingsPath);

  const orgProfile = useOrgMemberStore((s) => s.orgProfile);
  const { p: orgP } = useOrgPaths();
  const consumerNav = useUnifiedConsumerNav();

  const orgDashboardPath =
    orgProfile && consumerNav.useUnifiedNav ? orgP('/dashboard') ?? undefined : undefined;
  const breadcrumbHomeLabel =
    consumerNav.useUnifiedNav && !consumerNav.hasLessonsAccess && consumerNav.hasClubMemberAccess
      ? 'Member portal'
      : consumerNav.useUnifiedNav && consumerNav.hasLessonsAccess
        ? 'Student portal'
        : undefined;

  const breadcrumbs = useMemo(
    () =>
      resolveDashboardBreadcrumbs({
        dashboardRole,
        pathname: location.pathname,
        academySlug,
        title: header.title || undefined,
        custom: header.breadcrumbs ?? undefined,
        homePath: orgDashboardPath,
        homeLabel: breadcrumbHomeLabel,
        unifiedNavItems: consumerNav.useUnifiedNav ? consumerNav.navItems : undefined,
      }),
    [
      dashboardRole,
      location.pathname,
      academySlug,
      header.title,
      header.breadcrumbs,
      orgDashboardPath,
      breadcrumbHomeLabel,
      consumerNav.useUnifiedNav,
      consumerNav.navItems,
    ]
  );

  useEffect(() => {
    if (hasGuardianAccess && academySlug) {
      academyProfileService
        .getMyProfiles()
        .then((res) => {
          const profiles = res.data ?? [];
          setHasMultipleAcademies(profiles.length > 1);
        })
        .catch(() => setHasMultipleAcademies(false));
    }
  }, [hasGuardianAccess, academySlug]);

  const handleSignOut = async () => {
    await logout();
    const loginPath = p ? p('/login') : '/login';
    navigate(loginPath, { replace: true });
  };

  return (
    <header
      className="dashboard-content-chrome z-10 flex shrink-0 items-center gap-2 bg-card px-4 py-2.5 sm:px-6"
    >
      <SidebarTrigger
        className="h-8 w-8 shrink-0 rounded-md text-gray-600 hover:bg-transparent hover:text-gray-900 dark:text-gray-400 dark:hover:bg-transparent dark:hover:text-gray-200 md:hidden"
        aria-label="Toggle sidebar"
      />

      <DashboardBreadcrumbs items={breadcrumbs} />

      <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-2">
        {modeSwitch ? <DashboardModePill model={modeSwitch} /> : null}

        {user ? (
          <>
            <NotificationsPopover userId={user.id} viewAllPath={notificationsViewAllPath} />

            {header.help ? (
              <PageHelpPopover help={header.help} helpAnchorId={header.helpAnchorId} />
            ) : null}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 gap-2 px-2" aria-label="Account menu">
                  <UserAvatar
                    avatarUrl={user.avatarUrl}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    size="sm"
                  />
                  <span className="hidden max-w-[8rem] truncate text-sm text-muted-foreground lg:inline">
                    {user.firstName} {user.lastName}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[12rem]">
                <DropdownMenuLabel>
                  {user.firstName} {user.lastName}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {(dashboardRole === 'guardian' || dashboardRole === 'coach') &&
                  academySlug &&
                  hasMultipleAcademies && (
                    <DropdownMenuItem
                      onClick={() =>
                        navigate(dashboardRole === 'coach' ? '/coach/academy-picker' : '/academy-picker')
                      }
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      Switch Academy
                    </DropdownMenuItem>
                  )}
                <DropdownMenuItem
                  id={TOUR_STEP_IDS.ACCOUNT_MENU}
                  onClick={() => navigate(settingsPath)}
                  className={isSettingsActive ? 'bg-accent' : ''}
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Account
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Theme</DropdownMenuLabel>
                {THEME_MENU.map(({ value, label, Icon }) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => setTheme(value)}
                    className={theme === value ? 'bg-accent' : ''}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => void handleSignOut()}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : dashboardRole === 'guardian' && academySlug ? (
          <Button variant="outline" size="sm" asChild>
            <Link to={login?.(p?.('/lessons') ?? undefined) ?? (p ? p('/login') : '/login')}>
              <LogIn className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Student Login</span>
              <span className="sm:hidden">Login</span>
            </Link>
          </Button>
        ) : null}
      </div>
    </header>
  );
}
